import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Star, Briefcase, GraduationCap, ChevronLeft, ChevronRight, ArrowRight, CalendarClock,
} from "lucide-react";

export const ExpertShowcase = () => {
  const [experts, setExperts] = useState([]);
  const scroller = useRef(null);

  useEffect(() => {
    api.get("/experts").then((r) => setExperts(r.data)).catch(() => {});
  }, []);

  const scrollBy = (dir) => {
    scroller.current?.scrollBy({ left: dir * 340, behavior: "smooth" });
  };

  if (experts.length === 0) return null;

  return (
    <section className="bg-[#0F172A] text-slate-100 py-24" data-testid="expert-showcase">
      <div className="max-w-7xl mx-auto px-5">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-accent">Meet our experts</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight mt-2">Scroll to browse professionals</h2>
            <p className="text-slate-400 mt-2 max-w-xl">Swipe through vetted experts across every industry. Check their background, then book a call.</p>
          </div>
          <div className="hidden sm:flex gap-2">
            <button onClick={() => scrollBy(-1)} data-testid="showcase-prev" className="h-10 w-10 rounded-full border border-slate-700 flex items-center justify-center hover:bg-slate-800 transition-colors">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button onClick={() => scrollBy(1)} data-testid="showcase-next" className="h-10 w-10 rounded-full border border-slate-700 flex items-center justify-center hover:bg-slate-800 transition-colors">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div
          ref={scroller}
          className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth"
          style={{ scrollbarWidth: "thin" }}
          data-testid="showcase-scroller"
        >
          {experts.map((exp) => (
            <div
              key={exp.id}
              className="snap-start shrink-0 w-72 bg-[#1E293B] border border-slate-700 rounded-2xl overflow-hidden group"
              data-testid={`showcase-card-${exp.id}`}
            >
              <div className="relative h-56 overflow-hidden">
                <img src={exp.image} alt={exp.name} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1E293B] via-transparent to-transparent" />
                <div className="absolute top-3 left-3 bg-primary/90 backdrop-blur rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide">{exp.industry}</div>
                <div className="absolute top-3 right-3 bg-black/50 backdrop-blur rounded-full px-2.5 py-1 flex items-center gap-1 text-xs">
                  <Star className="h-3 w-3 text-accent fill-accent" /> {exp.rating}
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-heading font-semibold text-lg leading-tight">{exp.name}</h3>
                <p className="text-xs text-slate-300 flex items-start gap-1.5 mt-1.5"><Briefcase className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" /> {exp.current_designation}{exp.company ? ` · ${exp.company}` : ""}</p>
                {exp.education && <p className="text-xs text-slate-400 flex items-start gap-1.5 mt-1"><GraduationCap className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {exp.education}</p>}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(exp.specialties || []).slice(0, 3).map((s) => (
                    <Badge key={s} variant="secondary" className="bg-slate-700 text-slate-200 hover:bg-slate-700 text-[10px]">{s}</Badge>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-4">
                  <span className="font-heading font-semibold text-accent text-sm">{exp.rate}</span>
                  <Link to="/experts">
                    <Button size="sm" className="rounded-full h-8" data-testid={`showcase-book-${exp.id}`}>
                      <CalendarClock className="h-3.5 w-3.5 mr-1" /> Book
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Link to="/experts">
            <Button variant="outline" className="rounded-full font-semibold bg-transparent border-slate-700 text-slate-100 hover:bg-slate-800" data-testid="showcase-view-all">
              View all experts <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};
